/**
 * Zero Day Clock — reproduction of zerodayclock.com's visual grammar
 * against our own CISA KEV + NVD archive.
 *
 * Structure mirrors their site order:
 *   1. Hero — giant current-median-TTE counter (live-updating seconds since
 *      the most recent KEV addition)
 *   2. "The Collapse" progression — 2021 → today, year-by-year dramatic
 *      shift in units (days → hours → minutes), ending at the current number
 *   3. Log-scale decline chart
 *   4. Source provenance strip
 *   5. Explorer table — severity + source filters, sortable columns
 */
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Bug, Clock, Filter, RefreshCw, Search, Zap } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface YearStat {
  year: number
  count: number
  median_hours: number
  trimmed_mean_hours: number | null
  median_days: number
}
interface ReferencePoint {
  year: number
  mean_hours: number | null
  median_hours: number | null
  weaponized: number | null
}
interface Reference {
  by_year: ReferencePoint[]
  total_pairs: number
  sources: string[]
  captured_at: string
  site: string
}

interface Summary {
  total_records: number
  current_year: number
  current_median_hours: number | null
  current_median_days: number | null
  by_year: YearStat[]
  fit: { a: number | null; b: number | null; r2: number | null }
  latest: {
    cve_id: string; vendor: string; product: string;
    cve_date: string | null; exploit_date: string | null;
    tte_hours: number; cvss_score: number; severity: string;
  } | null
  source: { cisa_kev: boolean; vulncheck_kev: boolean; vulncheck_xdb: boolean; note: string }
  methodology_ref: string
  generated_at: string
}

interface ExplorerRecord {
  cve_id: string
  vendor: string
  product: string
  cve_date: string | null
  exploit_date: string | null
  tte_hours: number | null
  tte_days: number | null
  cvss_score: number | null
  severity: string | null
  ransomware_use: string | null
}

interface SourceMeta {
  key: string
  label: string
  enabled: boolean
  note?: string
}

interface Milestone {
  threshold: '1Y' | '1MO' | '1W' | '1D' | '1H' | '1MIN' | string
  threshold_days: number
  year: number | null
  status: 'reached' | 'projected'
}
interface ZeroDayRatePoint { year: number; pct: number; zero_day_count: number; exploited_count: number }
interface ExploitRatePoint { year: number; total_cves: number; exploited: number; pct: number }
interface SurvivalPoint    { day: number; pct: number }

interface Extras {
  milestones:    Milestone[]
  zero_day_rate: ZeroDayRatePoint[]
  exploit_rate:  ExploitRatePoint[]
  survival:      Record<string, SurvivalPoint[]>
}

const THRESHOLD_LABEL: Record<string, { big: string; small: string }> = {
  '1Y':   { big: '1', small: 'Year' },
  '1MO':  { big: '1', small: 'Month' },
  '1W':   { big: '1', small: 'Week' },
  '1D':   { big: '1', small: 'Day' },
  '1H':   { big: '1', small: 'Hour' },
  '1MIN': { big: '1', small: 'Minute' },
}

const SEVERITY_TONE: Record<string, string> = {
  critical: 'bg-rose/20 text-rose-light border-rose/40',
  high:     'bg-amber/20 text-amber-light border-amber/40',
  medium:   'bg-blue/20 text-blue-light border-blue/40',
  low:      'bg-text-muted/10 text-text-muted border-text-muted/20',
}

/** Format a TTE in hours. Preserves sign — a negative value means
 *  "exploited before disclosure" (zero-day), so "-18 HOURS" reads as a
 *  warning, not a cheerful "18 HOURS". */
function formatDuration(hoursIn: number | null | undefined): { value: string; unit: string } {
  if (hoursIn === null || hoursIn === undefined) return { value: '—', unit: '' }
  const sign = hoursIn < 0 ? '-' : ''
  const h = Math.abs(hoursIn)
  if (h < 1)        return { value: sign + (h * 60).toFixed(0),      unit: 'MINUTES' }
  if (h < 24)       return { value: sign + h.toFixed(1),              unit: 'HOURS' }
  if (h < 24 * 30)  return { value: sign + (h / 24).toFixed(1),       unit: 'DAYS' }
  if (h < 24 * 365) return { value: sign + (h / 24 / 30).toFixed(1),  unit: 'MONTHS' }
  return { value: sign + (h / 24 / 365).toFixed(1), unit: 'YEARS' }
}

function shortDur(h: number | null | undefined): string {
  if (h === null || h === undefined) return '—'
  const sign = h < 0 ? '-' : ''
  const abs = Math.abs(h)
  if (abs < 1)        return `${sign}${Math.round(abs * 60)}m`
  if (abs < 72)       return `${sign}${abs.toFixed(1)}h`
  if (abs < 24 * 365) return `${sign}${(abs / 24).toFixed(1)}d`
  return `${sign}${(abs / 24 / 365).toFixed(1)}y`
}

export default function ZeroDayClockPage() {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [extras, setExtras]   = useState<Extras | null>(null)
  const [reference, setReference] = useState<Reference | null>(null)
  const [sources, setSources] = useState<SourceMeta[]>([])
  const [records, setRecords] = useState<ExplorerRecord[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [severity, setSeverity] = useState<string>('')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('exploit_date')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  const [weaponisedOnly, setWeaponisedOnly] = useState(false)
  const [offset, setOffset] = useState(0)
  const [now, setNow] = useState(new Date())
  const limit = 50

  useEffect(() => {
    api.get('/api/zero-day-clock/summary')
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null))
    api.get('/api/zero-day-clock/meta')
      .then(r => setSources(r.data?.sources || []))
      .catch(() => setSources([]))
    api.get('/api/zero-day-clock/extras')
      .then(r => setExtras(r.data))
      .catch(() => setExtras(null))
    api.get('/api/zero-day-clock/reference')
      .then(r => setReference(r.data))
      .catch(() => setReference(null))
    // Live clock for the "time since last KEV addition" counter
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  const fetchExplorer = () => {
    setLoading(true)
    api.get('/api/zero-day-clock/explorer', {
      params: {
        limit, offset, severity: severity || undefined,
        q: query || undefined, sort, order,
        weaponised_only: weaponisedOnly ? 1 : undefined,
      },
    })
      .then(r => { setRecords(r.data?.records || []); setTotal(r.data?.total || 0) })
      .catch(() => { setRecords([]); setTotal(0) })
      .finally(() => setLoading(false))
  }
  useEffect(() => { fetchExplorer() }, [severity, query, sort, order, weaponisedOnly, offset])

  const hero = useMemo(() => formatDuration(summary?.current_median_hours), [summary])

  const sinceLastKev = useMemo(() => {
    if (!summary?.latest?.exploit_date) return null
    const ms = now.getTime() - new Date(summary.latest.exploit_date).getTime()
    const s = Math.floor(ms / 1000)
    const d = Math.floor(s / 86400)
    const h = Math.floor((s % 86400) / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return { d, h, m, s: sec, total: s }
  }, [summary, now])

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Nav-style section header — mirrors zerodayclock.com's tabs */}
      <div className="flex items-baseline gap-4 pb-2 border-b border-border">
        <h1 className="text-lg font-bold text-rose-light flex items-center gap-2">
          <Clock size={18} className="animate-pulse" /> ZERO DAY CLOCK
        </h1>
        <nav className="flex items-center gap-4 text-[11px] uppercase tracking-widest text-text-muted flex-wrap">
          <a href="#hero"        className="hover:text-text-primary">Dashboard</a>
          <a href="#collapse"    className="hover:text-text-primary">Collapse</a>
          <a href="#milestones"  className="hover:text-text-primary">Milestones</a>
          <a href="#zero-day-rate" className="hover:text-text-primary">Zero-Day Rate</a>
          <a href="#exploit-rate"  className="hover:text-text-primary">Exploit Rate</a>
          <a href="#survival"    className="hover:text-text-primary">Survival</a>
          <a href="#explorer"    className="hover:text-text-primary">Explorer</a>
          <a href={summary?.methodology_ref} target="_blank" rel="noreferrer" className="hover:text-text-primary">Methodology ↗</a>
        </nav>
      </div>

      {/* HERO — use the industry-standard zerodayclock.com numbers when we
          have them, so the headline matches what the world expects the
          "Zero Day Clock" to read. Our own CISA-only calculation is
          surfaced below as an auditable secondary reading. */}
      {(() => {
        const thisYear = summary?.current_year ?? 2026
        const ref = reference?.by_year.find(y => y.year === thisYear) || null
        const useRef = ref && (ref.mean_hours !== null || ref.median_hours !== null)
        const heroMedian = useRef && ref!.median_hours !== null ? ref!.median_hours : summary?.current_median_hours ?? null
        const heroMean   = useRef && ref!.mean_hours   !== null ? ref!.mean_hours   : null
        const display    = formatDuration(heroMedian)
        const isNeg      = heroMedian !== null && heroMedian !== undefined && heroMedian < 0
        return (
          <section id="hero" className="card p-6 bg-gradient-to-br from-rose/15 via-surface-card to-surface-card border-rose/40">
            <div className="text-[11px] uppercase tracking-[0.3em] text-rose-light font-semibold flex items-center gap-2">
              <Zap size={13} className="animate-pulse" />
              Current median time-to-exploit · {thisYear}
            </div>
            <div className="mt-3 flex items-end flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-baseline gap-3">
                <span className={clsx(
                  'text-[96px] leading-none font-black font-mono tabular-nums',
                  isNeg ? 'text-rose' : 'text-rose-light',
                )}>
                  {display.value}
                </span>
                <span className="text-2xl font-semibold text-rose/80 tracking-widest">{display.unit}</span>
              </div>
              {heroMean !== null && (
                <span className="text-sm text-text-muted font-mono pb-2">
                  mean (10% trimmed): <span className="text-text-primary">{shortDur(heroMean)}</span>
                </span>
              )}
            </div>
            {useRef && ref && (
              <div className="text-[10px] text-text-muted mt-2">
                Source: <a href={reference!.site} target="_blank" rel="noreferrer" className="text-blue-light hover:underline">zerodayclock.com</a>{' '}
                · {reference!.total_pairs.toLocaleString()} CVE-exploit pairs · {reference!.sources.join(' + ')}{' '}
                · sampled {reference!.captured_at}
                {ref.weaponized !== null && <> · <span className="text-rose-light font-semibold">{ref.weaponized}</span> weaponized this year</>}
              </div>
            )}
            <p className="text-xs text-text-muted mt-4 max-w-3xl leading-relaxed">
              Half of this year's KEV-confirmed exploits landed within this window of the CVE being published.
              A <span className="text-rose font-semibold">negative</span> number means half were already being
              exploited <em>before</em> disclosure — a zero-day. For scale, in 2018 the median was{' '}
              <span className="text-rose font-semibold">771 days</span>.
            </p>
          </section>
        )
      })()}

      {/* OUR FEED — secondary panel showing what the local CISA-only ingest
          actually computes, so numbers stay auditable and the gap vs the
          headline is transparent. */}
      <section className="card p-4 border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-text-muted font-semibold">Our own feed</div>
            <div className="text-xs text-text-secondary mt-1">
              CISA KEV only · {summary?.total_records?.toLocaleString() ?? '—'} CVE-exploit pairs
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <RefStat label="Median TTE"
              value={shortDur(summary?.current_median_hours)}
              tone={(summary?.current_median_hours ?? 0) < 0 ? 'rose' : 'default'} />
            <RefStat label="Latest KEV"
              value={summary?.latest ? summary.latest.cve_id : '—'} />
          </div>
        </div>
        <p className="text-[10px] text-text-muted mt-3">
          CISA confirms exploitation after the commercial feeds — VulnCheck KEV + XDB (in the headline) catch it
          earlier, so our local number reads more conservatively. Set{' '}
          <code className="font-mono text-text-primary">VULNCHECK_API_TOKEN</code> to blend in the commercial feed
          automatically via the <code className="font-mono">MIN(date_added)</code> join already wired in the base query.
        </p>
      </section>

      {/* ORIGINAL HERO block kept as a structural placeholder for the live
          "time since last KEV addition" ticker — moved below the new two
          heros so the industry number is the first thing a reader sees. */}
      <section id="hero-local" className="card p-4 bg-surface-card border-border">
        <div className="mt-0 flex items-end flex-wrap gap-x-6 gap-y-2">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-text-muted">
              {hero.value}
            </span>
            <span className="text-[10px] font-semibold text-text-muted tracking-widest">{hero.unit} (local)</span>
          </div>
          {summary?.current_median_days !== null && summary?.current_median_days !== undefined && (
            <span className="text-[11px] text-text-muted font-mono pb-1">
              ≈ {summary.current_median_days.toFixed(2)} days · locally-observed median from CVE disclosure to CISA KEV add
            </span>
          )}
        </div>

        <p className="text-xs text-text-muted mt-4 max-w-3xl leading-relaxed">
          Half of this year's KEV-confirmed exploits landed within this window of the CVE being published.
          A smaller number = defenders have less time to patch before attackers arrive. For scale, in 2018
          the median was <span className="text-rose font-semibold">771 days</span> (zerodayclock.com, CISA+VulnCheck blend).
        </p>

        {/* Live "since last KEV addition" counter — ticking every second */}
        {summary?.latest && sinceLastKev && (
          <div className="mt-5 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-muted">Time since last KEV addition</div>
              <div className="text-3xl font-black font-mono text-amber-light tabular-nums mt-1">
                {sinceLastKev.d}d {String(sinceLastKev.h).padStart(2, '0')}h {String(sinceLastKev.m).padStart(2, '0')}m {String(sinceLastKev.s).padStart(2, '0')}s
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-text-muted">Last addition</div>
              <div className="font-mono font-bold text-rose-light mt-1">{summary.latest.cve_id}</div>
              <div className="text-[11px] text-text-primary">
                {summary.latest.vendor} · {summary.latest.product}
              </div>
              <div className="text-[10px] text-text-muted mt-0.5">
                TTE was {shortDur(summary.latest.tte_hours)} ·{' '}
                <span className={clsx('uppercase', summary.latest.severity === 'critical' ? 'text-rose' : 'text-amber-light')}>
                  {summary.latest.severity}
                </span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* THE COLLAPSE — zerodayclock.com "From Vulnerability to Exploitation" combo chart */}
      <section id="collapse" className="card p-6">
        <div className="mb-1">
          <h2 className="text-2xl font-bold text-text-primary">From Vulnerability to Exploitation</h2>
          <p className="text-sm text-text-muted mt-1">
            TTE (Time-to-Exploit) measures the gap between CVE disclosure and confirmed exploitation
          </p>
        </div>

        {summary && summary.by_year.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-[11px]">
              <LegendSwatch kind="line-solid"  color="#f5f5f4" label="Mean TTE"   sub="(10% trimmed, days)" />
              <LegendSwatch kind="line-dashed" color="#94a3b8" label="Median TTE" sub="(days)" />
              <LegendSwatch kind="bar"         color="#f43f5e" label="Weaponized Exploits" sub="(count)" />
            </div>

            <TteComboChart data={summary.by_year} />

            <div className="mt-4 pt-3 border-t border-border text-[10px] text-text-muted flex items-center justify-between flex-wrap gap-2">
              <span>
                Based on <span className="font-mono font-semibold text-text-primary">{summary.total_records.toLocaleString()}</span>{' '}
                CVE-exploit pairs from trusted sources (CISA KEV).
                {summary.fit.a !== null && summary.fit.b !== null && (
                  <>
                    {' '}Fit: TTE(y) = {summary.fit.a.toFixed(1)}h · e<sup>−{summary.fit.b.toFixed(3)}·(y−2018)</sup> · R² = {summary.fit.r2}
                  </>
                )}
              </span>
              <a href={summary.methodology_ref} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 text-rose-light hover:underline">
                <span className="w-1.5 h-1.5 rounded-full bg-rose animate-pulse" />
                zerodayclock.com methodology ↗
              </a>
            </div>
          </>
        )}
      </section>

      {/* MILESTONES — 1Y / 1MO / 1W / 1D / 1H / 1MIN */}
      {extras && extras.milestones.length > 0 && (
        <section id="milestones" className="card p-6">
          <h2 className="text-2xl font-bold text-text-primary">Time-to-Exploit Milestones</h2>
          <p className="text-sm text-text-muted mt-1">When mean time-to-exploit crosses each threshold</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
            {extras.milestones.map(m => (
              <MilestoneCard key={m.threshold} m={m} />
            ))}
          </div>
        </section>
      )}

      {/* ZERO-DAY RATE */}
      {extras && extras.zero_day_rate.length > 0 && (
        <section id="zero-day-rate" className="card p-6">
          <h2 className="text-2xl font-bold text-text-primary">Zero-Day Rate</h2>
          <p className="text-sm text-text-muted mt-1">
            Percentage of exploited CVEs where exploitation occurred before or on the day of disclosure (TTE ≤ 0)
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-[11px]">
            <LegendSwatch kind="line-solid" color="#f43f5e" label="Zero-Day Rate" sub="(% of exploited)" />
            <LegendSwatch kind="bar"        color="#94a3b8" label="Exploited CVEs" sub="(count)" />
          </div>
          <ZeroDayRateChart data={extras.zero_day_rate} />
          {extras.zero_day_rate.length >= 2 && (() => {
            const first = extras.zero_day_rate[0]
            const last  = extras.zero_day_rate[extras.zero_day_rate.length - 1]
            return (
              <div className="mt-3 pt-3 border-t border-border text-[10px] text-text-muted flex items-center justify-between flex-wrap gap-2">
                <span>
                  <span className="text-rose-light font-semibold">{last.pct.toFixed(1)}%</span>{' '}
                  of exploited CVEs in {last.year} are zero-days, up from{' '}
                  <span className="font-semibold">{first.pct.toFixed(1)}%</span> in {first.year}.
                </span>
                <span className="inline-flex items-center gap-1.5 text-rose-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose animate-pulse" /> zerodayclock.com
                </span>
              </div>
            )
          })()}
        </section>
      )}

      {/* EXPLOIT RATE */}
      {extras && extras.exploit_rate.length > 0 && (
        <section id="exploit-rate" className="card p-6">
          <h2 className="text-2xl font-bold text-text-primary">Exploit Rate</h2>
          <p className="text-sm text-text-muted mt-1">
            Percentage of all published CVEs that are confirmed exploited in the wild, versus total CVE volume
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-[11px]">
            <LegendSwatch kind="line-solid" color="#f5f5f4" label="Exploit Rate" sub="(% of all CVEs)" />
            <LegendSwatch kind="bar"        color="#94a3b8" label="Total CVEs Published" sub="(count)" />
          </div>
          <ExploitRateChart data={extras.exploit_rate} />
          {(() => {
            const recent = extras.exploit_rate[extras.exploit_rate.length - 1]
            const prior  = extras.exploit_rate[extras.exploit_rate.length - 2]
            const yoy = prior ? ((recent.total_cves - prior.total_cves) / prior.total_cves) * 100 : 0
            return (
              <div className="mt-3 pt-3 border-t border-border text-[10px] text-text-muted">
                <span className="font-mono font-semibold text-text-primary">{recent.total_cves.toLocaleString()}</span> CVEs
                published in {recent.year}, {recent.exploited.toLocaleString()} exploited (
                <span className="font-semibold">{recent.pct.toFixed(2)}%</span>). CVE volume grew{' '}
                <span className={clsx('font-semibold', yoy >= 0 ? 'text-emerald' : 'text-rose')}>
                  {yoy >= 0 ? '+' : ''}{yoy.toFixed(0)}%
                </span>{' '}year-over-year.
              </div>
            )
          })()}
        </section>
      )}

      {/* SURVIVAL CURVES */}
      {extras && Object.keys(extras.survival).length > 0 && (
        <section id="survival" className="card p-6">
          <h2 className="text-2xl font-bold text-text-primary">Exploit Survival Curve</h2>
          <p className="text-sm text-text-muted mt-1">
            Of all CVEs that will eventually be exploited, what percentage remain unexploited at each point in time after disclosure?
          </p>
          <div className="mt-3 p-3 rounded-md bg-surface-raised/60 border border-border text-xs text-text-secondary leading-relaxed">
            <span className="font-semibold text-text-primary">How to read:</span>{' '}
            Each line tracks one year's cohort of exploited CVEs. The Y-axis shows the percentage still
            "surviving" (not yet exploited). A steeper drop means faster exploitation. When a line hits 50%,
            half of that year's exploited CVEs were already compromised. The curves are collapsing leftward
            year over year — the traditional 30-day patch window is disappearing.
          </div>
          <SurvivalCurveChart cohorts={extras.survival} />
        </section>
      )}

      {/* SOURCE PROVENANCE STRIP */}
      {sources.length > 0 && (
        <section className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-2">Data sources</div>
          <div className="flex flex-wrap gap-1.5">
            {sources.map(s => (
              <span key={s.key} title={s.note}
                className={clsx(
                  'badge border text-[10px] px-2 py-0.5',
                  s.enabled
                    ? 'bg-emerald/10 border-emerald/40 text-emerald'
                    : 'bg-text-muted/5 border-text-muted/20 text-text-muted/60 line-through',
                )}>
                {s.enabled ? '✓ ' : ''}{s.label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* EXPLORER */}
      <section id="explorer" className="card">
        <div className="flex items-center gap-2 p-3 border-b border-border flex-wrap">
          <Bug size={14} className="text-rose" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Explorer</h2>
          <span className="text-[10px] text-text-muted font-mono">{total.toLocaleString()} records</span>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-md border border-border overflow-hidden text-[10px]">
              {['', 'critical', 'high', 'medium', 'low'].map(s => (
                <button key={s || 'all'} type="button" onClick={() => { setSeverity(s); setOffset(0) }}
                  className={clsx(
                    'px-2.5 py-1 uppercase tracking-widest',
                    severity === s ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-raised',
                  )}>
                  {s || 'all'}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => { setWeaponisedOnly(v => !v); setOffset(0) }}
              className={clsx(
                'px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest border inline-flex items-center gap-1.5',
                weaponisedOnly
                  ? 'bg-rose/20 text-rose-light border-rose/40'
                  : 'text-text-muted border-border hover:bg-surface-raised',
              )}
              title="Ransomware-linked only">
              <Filter size={11} /> Weaponised
            </button>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
              <input className="input pl-7 w-[200px]"
                placeholder="Vendor / product / CVE"
                value={query} onChange={e => { setQuery(e.target.value); setOffset(0) }} />
            </div>
            <button type="button" onClick={fetchExplorer} className="btn-ghost" aria-label="Refresh explorer" title="Refresh">
              <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-text-muted">
              <tr>
                <Th label="CVE"              sortKey="cve_id"       sort={sort} order={order} setSort={setSort} setOrder={setOrder} />
                <Th label="Vendor · Product" />
                <Th label="CVE date"         sortKey="cve_date"     sort={sort} order={order} setSort={setSort} setOrder={setOrder} />
                <Th label="Exploit date"     sortKey="exploit_date" sort={sort} order={order} setSort={setSort} setOrder={setOrder} />
                <Th label="TTE"              sortKey="tte"          sort={sort} order={order} setSort={setSort} setOrder={setOrder} align="right" />
                <Th label="CVSS"             sortKey="cvss"         sort={sort} order={order} setSort={setSort} setOrder={setOrder} align="right" />
                <Th label="Severity" />
                <Th label="Ransomware" />
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.cve_id + (r.exploit_date || '')} className="border-t border-border hover:bg-surface-raised/50">
                  <td className="px-3 py-1.5 font-mono">
                    <a href={`/app/cve/${r.cve_id}`} className="text-blue-light hover:underline">{r.cve_id}</a>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="text-text-primary">{r.vendor || '—'}</div>
                    <div className="text-[10px] text-text-muted">{r.product || ''}</div>
                  </td>
                  <td className="px-3 py-1.5 text-text-muted font-mono">{r.cve_date?.slice(0, 10) || '—'}</td>
                  <td className="px-3 py-1.5 text-text-muted font-mono">{r.exploit_date?.slice(0, 10) || '—'}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">
                    <span className={clsx(
                      (r.tte_hours ?? 99999) < 24      ? 'text-rose' :
                      (r.tte_hours ?? 99999) < 24 * 7  ? 'text-rose-light' :
                      (r.tte_hours ?? 99999) < 24 * 30 ? 'text-amber-light' :
                                                          'text-text-muted',
                    )}>
                      {shortDur(r.tte_hours)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-text-muted">{r.cvss_score?.toFixed(1) || '—'}</td>
                  <td className="px-3 py-1.5">
                    {r.severity && (
                      <span className={clsx('badge border', SEVERITY_TONE[r.severity] || SEVERITY_TONE.medium)}>
                        {r.severity}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-text-muted">{r.ransomware_use === 'Known' ? 'Known' : '—'}</td>
                </tr>
              ))}
              {records.length === 0 && !loading && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-text-muted italic">
                  No records for this filter. Try widening the severity filter or clearing the search.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 border-t border-border text-[10px] text-text-muted">
          <span>
            Showing {total === 0 ? 0 : offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
              className="btn-ghost disabled:opacity-30">← Prev</button>
            <button type="button" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}
              className="btn-ghost disabled:opacity-30">Next →</button>
          </div>
        </div>
      </section>
    </div>
  )
}

/** Legend swatch for the combo chart — matches zerodayclock.com's
 *  solid / dashed / bar icons next to each series name. */
function LegendSwatch({ kind, color, label, sub }: {
  kind: 'line-solid' | 'line-dashed' | 'bar'; color: string; label: string; sub?: string;
}) {
  return (
    <div className="inline-flex items-center gap-1.5">
      {kind === 'bar' ? (
        <span className="inline-block w-3 h-2.5 rounded-sm" style={{ background: color, opacity: 0.35 }} />
      ) : (
        <svg width="20" height="8" className="inline-block">
          <line x1="0" y1="4" x2="20" y2="4" stroke={color} strokeWidth="1.75"
            strokeDasharray={kind === 'line-dashed' ? '3 2' : undefined} />
        </svg>
      )}
      <span className="text-text-primary font-medium">{label}</span>
      {sub && <span className="text-text-muted text-[10px]">{sub}</span>}
    </div>
  )
}

/** TTE combo chart — rose bars for weaponized count (right axis), solid line
 *  for mean TTE, dashed line for median TTE (left axis in days). Mirrors the
 *  "From Vulnerability to Exploitation" chart on zerodayclock.com. */
function TteComboChart({ data }: { data: YearStat[] }) {
  const [hover, setHover] = useState<number | null>(null)

  const W = 900, H = 360
  const PAD_L = 56, PAD_R = 56, PAD_T = 30, PAD_B = 40

  const years = useMemo(() => [...data].sort((a, b) => a.year - b.year), [data])
  if (years.length === 0) return null

  // Left axis domain: max TTE in days, padded. Use mean trimmed OR median,
  // whichever is bigger so neither line ever clips.
  const teaDays = (h: number) => h / 24
  const maxTteDays = Math.max(
    1,
    ...years.map(y => Math.max(teaDays(y.median_hours), teaDays(y.trimmed_mean_hours ?? y.median_hours))),
  )
  const tteMax = maxTteDays * 1.1

  // Right axis domain: weaponized counts.
  const maxCount = Math.max(1, ...years.map(y => y.count))
  const countMax = maxCount * 1.15

  const xBand = (W - PAD_L - PAD_R) / years.length
  const xCenter = (i: number) => PAD_L + xBand * (i + 0.5)
  const yLeft  = (d: number) => PAD_T + (1 - d / tteMax) * (H - PAD_T - PAD_B)
  const yRight = (c: number) => PAD_T + (1 - c / countMax) * (H - PAD_T - PAD_B)

  // Nice-number ticks: pick 4 evenly-spaced marks on each axis.
  const leftTicks = [0, 1, 2, 3].map(i => (tteMax * i) / 3)
  const rightTicks = [0, 1, 2, 3].map(i => Math.round((countMax * i) / 3))

  // Format a TTE in days to a compact label that auto-shifts units.
  // Preserves sign so a NEGATIVE TTE (exploited before disclosure — a
  // zero-day) renders as e.g. "-18h", not a misleading positive number.
  const tteLabel = (days: number): string => {
    if (days === 0) return '0'
    const sign = days < 0 ? '-' : ''
    const abs = Math.abs(days)
    if (abs < 1 / 24) return `${sign}${Math.round(abs * 24 * 60)}m`
    if (abs < 1)      return `${sign}${Math.round(abs * 24)}h`
    if (abs < 30)     return `${sign}${abs.toFixed(abs < 10 ? 1 : 0)}d`
    if (abs < 365)    return `${sign}${(abs / 30).toFixed(1)}mo`
    return `${sign}${(abs / 365).toFixed(1)}y`
  }

  const meanPath = years.map((y, i) => {
    const d = teaDays(y.trimmed_mean_hours ?? y.median_hours)
    return `${i === 0 ? 'M' : 'L'}${xCenter(i)},${yLeft(d)}`
  }).join(' ')

  const medianPath = years.map((y, i) =>
    `${i === 0 ? 'M' : 'L'}${xCenter(i)},${yLeft(teaDays(y.median_hours))}`
  ).join(' ')

  return (
    <div className="relative overflow-x-auto mt-4">
      <svg width={W} height={H} className="max-w-full">
        <defs>
          <linearGradient id="zdc-bar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#f43f5e" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.15" />
          </linearGradient>
        </defs>

        {/* Horizontal gridlines + left-axis tick labels (days) */}
        {leftTicks.map((t, i) => (
          <g key={`L${i}`}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yLeft(t)} y2={yLeft(t)}
              stroke="#27272a" strokeDasharray={i === 0 ? undefined : '2 4'} />
            <text x={PAD_L - 8} y={yLeft(t) + 3} fontSize="10" fill="#94a3b8" textAnchor="end">
              {tteLabel(t)}
            </text>
          </g>
        ))}

        {/* Right-axis tick labels (weaponized count) */}
        {rightTicks.map((t, i) => (
          <g key={`R${i}`}>
            <text x={W - PAD_R + 8} y={yRight(t) + 3} fontSize="10" fill="#fb7185" textAnchor="start">
              {t.toLocaleString()}
            </text>
          </g>
        ))}
        <text x={18} y={PAD_T - 12} fontSize="9" fill="#71717a">TTE (days)</text>
        <text x={W - 10} y={PAD_T - 12} fontSize="9" fill="#fb7185" textAnchor="end">Weaponized</text>

        {/* Weaponized bars (right axis) */}
        {years.map((y, i) => {
          const barTop = yRight(y.count)
          const barH   = yRight(0) - barTop
          const barW   = Math.min(48, xBand * 0.55)
          const barX   = xCenter(i) - barW / 2
          return (
            <rect key={`bar-${y.year}`}
              x={barX} y={barTop} width={barW} height={Math.max(0, barH)}
              fill="url(#zdc-bar)" rx={3}
              opacity={hover === null || hover === i ? 1 : 0.35} />
          )
        })}

        {/* Median TTE line — dashed grey, rendered under the mean line */}
        <path d={medianPath} fill="none" stroke="#94a3b8" strokeWidth="1.75"
          strokeDasharray="4 3" opacity="0.9" />

        {/* Mean TTE line — solid whitesmoke (reads as "black on dark" equivalent) */}
        <path d={meanPath} fill="none" stroke="#f5f5f4" strokeWidth="2.25" />

        {/* Median dots */}
        {years.map((y, i) => (
          <circle key={`med-${y.year}`} cx={xCenter(i)} cy={yLeft(teaDays(y.median_hours))}
            r="3" fill="#94a3b8" opacity="0.85" />
        ))}

        {/* Mean dots + data-point labels (mirrors the "2.3y / 56d / 10h" annotations) */}
        {years.map((y, i) => {
          const mean = teaDays(y.trimmed_mean_hours ?? y.median_hours)
          const cx = xCenter(i)
          const cy = yLeft(mean)
          return (
            <g key={`mean-${y.year}`}>
              <circle cx={cx} cy={cy} r="4" fill="#f5f5f4" stroke="#0a0a0a" strokeWidth="1.5" />
              <text x={cx} y={cy - 10} fontSize="11" fill="#f5f5f4" textAnchor="middle" fontWeight="600">
                {tteLabel(mean)}
              </text>
            </g>
          )
        })}

        {/* X-axis year labels */}
        {years.map((y, i) => (
          <text key={`x-${y.year}`} x={xCenter(i)} y={H - PAD_B + 18}
            fontSize="11" fill="#94a3b8" textAnchor="middle" fontWeight="500">
            {y.year}
          </text>
        ))}

        {/* Hover hit-regions — one per year for tooltip targeting */}
        {years.map((_, i) => (
          <rect key={`hit-${i}`}
            x={PAD_L + xBand * i} y={PAD_T}
            width={xBand} height={H - PAD_T - PAD_B}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)} />
        ))}
      </svg>

      {/* Hover card — shows the year's stats like zerodayclock.com's tooltip */}
      {hover !== null && (() => {
        const y = years[hover]
        const mean = teaDays(y.trimmed_mean_hours ?? y.median_hours)
        const med  = teaDays(y.median_hours)
        const cx = xCenter(hover)
        // Place the card to the right of the hovered bar, or to the left on the
        // last two years so it doesn't fall off the chart.
        const placeLeft = hover >= years.length - 2
        return (
          <div className="absolute pointer-events-none card bg-surface-card/95 backdrop-blur-md border border-border p-3 min-w-[220px] text-xs shadow-xl"
            style={{
              left: placeLeft ? `max(0px, ${cx - 230}px)` : `${cx + 14}px`,
              top:  `${PAD_T + 8}px`,
            }}>
            <div className="text-base font-bold text-text-primary">{y.year}</div>
            <div className="mt-2 space-y-1 font-mono">
              <RowWithDot dot="solid" color="#f5f5f4" label="Mean TTE" sub="(trimmed)" value={tteLabel(mean)} />
              <RowWithDot dot="dashed" color="#94a3b8" label="Median TTE" value={tteLabel(med)} />
              <RowWithDot dot="bar"    color="#f43f5e" label="Weaponized" value={y.count.toLocaleString()} />
            </div>
            <div className="mt-2 pt-2 border-t border-border text-[10px] text-text-muted">
              {y.count} CVEs tracked
            </div>
          </div>
        )
      })()}
    </div>
  )
}

/** Single reference stat block — label above, big value below. */
function RefStat({ label, value, tone = 'default' }: {
  label: string; value: string; tone?: 'default' | 'rose';
}) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx(
        'text-xl font-black font-mono tabular-nums',
        tone === 'rose' ? 'text-rose-light' : 'text-text-primary',
      )}>{value}</div>
    </div>
  )
}

function RowWithDot({ dot, color, label, sub, value }: {
  dot: 'solid' | 'dashed' | 'bar'; color: string; label: string; sub?: string; value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-text-muted">
        {dot === 'bar' ? (
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color, opacity: 0.5 }} />
        ) : (
          <svg width="14" height="6"><line x1="0" y1="3" x2="14" y2="3" stroke={color} strokeWidth="1.5"
            strokeDasharray={dot === 'dashed' ? '3 2' : undefined} /></svg>
        )}
        <span className="text-text-primary">{label}</span>
        {sub && <span className="text-[9px]">{sub}</span>}
      </span>
      <span className="font-semibold text-text-primary tabular-nums">{value}</span>
    </div>
  )
}

function Th({ label, sortKey, sort, order, setSort, setOrder, align = 'left' }: {
  label: string; sortKey?: string; sort?: string; order?: 'asc' | 'desc';
  setSort?: (s: string) => void; setOrder?: (o: 'asc' | 'desc') => void;
  align?: 'left' | 'right';
}) {
  const active = !!sortKey && sort === sortKey
  const click = () => {
    if (!sortKey || !setSort || !setOrder) return
    if (active) setOrder(order === 'asc' ? 'desc' : 'asc')
    else { setSort(sortKey); setOrder('desc') }
  }
  return (
    <th className={clsx('px-3 py-2 text-[10px] font-semibold uppercase tracking-widest', align === 'right' ? 'text-right' : 'text-left')}>
      {sortKey ? (
        <button type="button" onClick={click} className={clsx('inline-flex items-center gap-1', active ? 'text-blue-light' : 'hover:text-text-primary')}>
          {label}{active && (order === 'desc' ? ' ▼' : ' ▲')}
        </button>
      ) : label}
    </th>
  )
}

function YearDecline({ data }: { data: YearStat[] }) {
  const W = 800, H = 200, PAD_L = 48, PAD_R = 16, PAD_T = 18, PAD_B = 30
  const values = useMemo(() => data.map(d => d.median_hours).filter(v => v > 0), [data])
  const maxLog = Math.log10(Math.max(10, ...values))
  const minLog = 0
  const xFor = (i: number) => PAD_L + (i / Math.max(1, data.length - 1)) * (W - PAD_L - PAD_R)
  const yFor = (v: number) => {
    if (v <= 0) return H - PAD_B
    const l = Math.log10(v)
    return PAD_T + (1 - (l - minLog) / (maxLog - minLog)) * (H - PAD_T - PAD_B)
  }
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(d.median_hours)}`).join(' ')
  const area = path + ` L${xFor(data.length - 1)},${H - PAD_B} L${xFor(0)},${H - PAD_B} Z`

  return (
    <div className="overflow-x-auto">
      <svg width={W} height={H} className="max-w-full">
        {/* Log gridlines: 1h, 1d, 1w, 1mo, 1y */}
        {[1, 24, 168, 720, 8760].map(v => (
          <g key={v}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yFor(v)} y2={yFor(v)} stroke="#27272a" strokeDasharray="2 3" />
            <text x={PAD_L - 6} y={yFor(v) + 3} fontSize="9" fill="#71717a" textAnchor="end">
              {v === 1 ? '1h' : v === 24 ? '1d' : v === 168 ? '1w' : v === 720 ? '1mo' : '1y'}
            </text>
          </g>
        ))}
        <defs>
          <linearGradient id="zdc-fade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#f43f5e" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#zdc-fade)" />
        <path d={path} fill="none" stroke="#f43f5e" strokeWidth="2" />
        {data.map((d, i) => (
          <g key={d.year}>
            <circle cx={xFor(i)} cy={yFor(d.median_hours)} r={3.5} fill="#f43f5e" />
            <text x={xFor(i)} y={H - 12} fontSize="10" fill="#94a3b8" textAnchor="middle">{d.year}</text>
            <title>{d.year}: median {d.median_hours.toFixed(1)}h · n={d.count}</title>
          </g>
        ))}
      </svg>
    </div>
  )
}

/** Milestone card — single threshold crossing (e.g. "1 Hour · Projected ~2026"). */
function MilestoneCard({ m }: { m: Milestone }) {
  const l = THRESHOLD_LABEL[m.threshold] || { big: m.threshold, small: '' }
  const reached = m.status === 'reached'
  return (
    <div className={clsx(
      'rounded-lg p-4 border text-center',
      reached ? 'bg-emerald/5 border-emerald/30' : 'bg-surface-raised/40 border-border',
    )}>
      <div className="text-5xl font-black font-mono tabular-nums text-text-primary leading-none">{l.big}</div>
      <div className="text-lg font-semibold text-text-primary mt-0.5">{l.small}</div>
      <div className={clsx(
        'inline-block mt-2 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-widest uppercase',
        reached ? 'bg-emerald/20 text-emerald' : 'bg-rose/15 text-rose-light',
      )}>
        {reached ? 'Reached' : 'Projected'}
      </div>
      <div className="mt-1 text-sm font-mono text-text-primary">
        {m.year ? `~${m.year}` : '—'}
      </div>
    </div>
  )
}

/** Zero-Day Rate combo chart — rose line for % + grey bars for exploited count. */
function ZeroDayRateChart({ data }: { data: ZeroDayRatePoint[] }) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 900, H = 340
  const PAD_L = 56, PAD_R = 56, PAD_T = 20, PAD_B = 38
  const years = useMemo(() => [...data].sort((a, b) => a.year - b.year), [data])
  if (years.length === 0) return null

  const pctMax = 100
  const countMax = Math.max(1, ...years.map(y => y.exploited_count)) * 1.15

  const xBand = (W - PAD_L - PAD_R) / years.length
  const xCenter = (i: number) => PAD_L + xBand * (i + 0.5)
  const yLeft = (p: number) => PAD_T + (1 - p / pctMax) * (H - PAD_T - PAD_B)
  const yRight = (c: number) => PAD_T + (1 - c / countMax) * (H - PAD_T - PAD_B)

  const leftTicks = [0, 25, 50, 75, 100]
  const rightTicks = [0, 1, 2, 3].map(i => Math.round((countMax * i) / 3))

  const pctPath = years.map((y, i) =>
    (i === 0 ? 'M' : 'L') + xCenter(i) + ',' + yLeft(y.pct)
  ).join(' ')

  return (
    <div className="relative overflow-x-auto mt-3">
      <svg width={W} height={H} className="max-w-full">
        <defs>
          <linearGradient id="zdr-line-fade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#f43f5e" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
          </linearGradient>
        </defs>

        {leftTicks.map(t => (
          <g key={'L' + t}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yLeft(t)} y2={yLeft(t)}
              stroke={t === 50 ? '#f43f5e' : '#27272a'}
              strokeDasharray={t === 0 ? undefined : t === 50 ? '3 4' : '2 4'}
              opacity={t === 50 ? 0.35 : 1} />
            <text x={PAD_L - 8} y={yLeft(t) + 3} fontSize="10" fill="#94a3b8" textAnchor="end">{t}%</text>
          </g>
        ))}
        {rightTicks.map((t, i) => (
          <text key={'R' + i} x={W - PAD_R + 8} y={yRight(t) + 3}
            fontSize="10" fill="#94a3b8" textAnchor="start">{t.toLocaleString()}</text>
        ))}
        <text x={18} y={PAD_T - 8} fontSize="9" fill="#71717a">Zero-Day Rate (%)</text>
        <text x={W - 10} y={PAD_T - 8} fontSize="9" fill="#71717a" textAnchor="end">CVE count</text>

        {years.map((y, i) => {
          const barTop = yRight(y.exploited_count)
          const barH = yRight(0) - barTop
          const barW = Math.min(52, xBand * 0.55)
          return (
            <rect key={'bar-' + y.year}
              x={xCenter(i) - barW / 2} y={barTop}
              width={barW} height={Math.max(0, barH)}
              fill="#94a3b8" opacity={hover === null || hover === i ? 0.3 : 0.12}
              rx={3} />
          )
        })}

        <path d={pctPath + ' L' + xCenter(years.length - 1) + ',' + (H - PAD_B) + ' L' + xCenter(0) + ',' + (H - PAD_B) + ' Z'}
          fill="url(#zdr-line-fade)" />
        <path d={pctPath} fill="none" stroke="#f43f5e" strokeWidth="2.25" />

        {years.map((y, i) => (
          <g key={'p-' + y.year}>
            <circle cx={xCenter(i)} cy={yLeft(y.pct)} r={3.5} fill="#f43f5e" />
            <text x={xCenter(i)} y={yLeft(y.pct) - 9} fontSize="10" fill="#fda4af"
              textAnchor="middle" fontWeight="600">{y.pct.toFixed(1)}%</text>
          </g>
        ))}

        {years.map((y, i) => (
          <text key={'x-' + y.year} x={xCenter(i)} y={H - PAD_B + 16}
            fontSize="11" fill="#94a3b8" textAnchor="middle" fontWeight="500">{y.year}</text>
        ))}

        {years.map((_, i) => (
          <rect key={'hit-' + i}
            x={PAD_L + xBand * i} y={PAD_T}
            width={xBand} height={H - PAD_T - PAD_B}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)} />
        ))}
      </svg>

      {hover !== null && (() => {
        const y = years[hover]
        const placeLeft = hover >= years.length - 2
        const left = placeLeft ? Math.max(0, xCenter(hover) - 210) : xCenter(hover) + 14
        return (
          <div className="absolute pointer-events-none card bg-surface-card/95 backdrop-blur-md border border-border p-3 min-w-[200px] text-xs shadow-xl"
            style={{ left, top: PAD_T + 8 }}>
            <div className="text-base font-bold">{y.year}</div>
            <div className="mt-2 space-y-1 font-mono">
              <RowWithDot dot="solid" color="#f43f5e" label="Zero-Day Rate" value={y.pct.toFixed(1) + '%'} />
              <RowWithDot dot="bar"   color="#94a3b8" label="Exploited"     value={y.exploited_count.toLocaleString()} />
              <RowWithDot dot="bar"   color="#f43f5e" label="Zero-Day"      value={y.zero_day_count.toLocaleString()} />
            </div>
          </div>
        )
      })()}
    </div>
  )
}

/** Exploit Rate combo chart — white line for exploit rate % + grey bars for
 *  total CVE volume. Same shape as ZeroDayRateChart but different domain. */
function ExploitRateChart({ data }: { data: ExploitRatePoint[] }) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 900, H = 340
  const PAD_L = 56, PAD_R = 56, PAD_T = 20, PAD_B = 38
  const years = useMemo(() => [...data].sort((a, b) => a.year - b.year), [data])
  if (years.length === 0) return null

  const pctMax = Math.max(1, ...years.map(y => y.pct)) * 1.2
  const totalMax = Math.max(1, ...years.map(y => y.total_cves)) * 1.15

  const xBand = (W - PAD_L - PAD_R) / years.length
  const xCenter = (i: number) => PAD_L + xBand * (i + 0.5)
  const yLeft = (p: number) => PAD_T + (1 - p / pctMax) * (H - PAD_T - PAD_B)
  const yRight = (c: number) => PAD_T + (1 - c / totalMax) * (H - PAD_T - PAD_B)

  const leftTicks = [0, 1, 2, 3, 4].map(i => (pctMax * i) / 4)
  const rightTicks = [0, 1, 2, 3].map(i => Math.round((totalMax * i) / 3 / 1000) * 1000)

  const pctPath = years.map((y, i) =>
    (i === 0 ? 'M' : 'L') + xCenter(i) + ',' + yLeft(y.pct)
  ).join(' ')

  const fmtThousands = (n: number) => n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : n.toString()

  return (
    <div className="relative overflow-x-auto mt-3">
      <svg width={W} height={H} className="max-w-full">
        {leftTicks.map((t, i) => (
          <g key={'L' + i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yLeft(t)} y2={yLeft(t)}
              stroke="#27272a" strokeDasharray={i === 0 ? undefined : '2 4'} />
            <text x={PAD_L - 8} y={yLeft(t) + 3} fontSize="10" fill="#94a3b8" textAnchor="end">
              {t.toFixed(t < 1 ? 2 : 1)}%
            </text>
          </g>
        ))}
        {rightTicks.map((t, i) => (
          <text key={'R' + i} x={W - PAD_R + 8} y={yRight(t) + 3}
            fontSize="10" fill="#94a3b8" textAnchor="start">{fmtThousands(t)}</text>
        ))}
        <text x={18} y={PAD_T - 8} fontSize="9" fill="#71717a">Total CVEs</text>
        <text x={W - 10} y={PAD_T - 8} fontSize="9" fill="#71717a" textAnchor="end">Exploit Rate (%)</text>

        {years.map((y, i) => {
          const barTop = yRight(y.total_cves)
          const barH = yRight(0) - barTop
          const barW = Math.min(52, xBand * 0.55)
          return (
            <g key={'bar-' + y.year}>
              <rect x={xCenter(i) - barW / 2} y={barTop}
                width={barW} height={Math.max(0, barH)}
                fill="#94a3b8" opacity={hover === null || hover === i ? 0.3 : 0.12} rx={3} />
              <text x={xCenter(i)} y={barTop - 4} fontSize="10" fill="#94a3b8"
                textAnchor="middle" opacity="0.7">
                {fmtThousands(y.total_cves)}
              </text>
            </g>
          )
        })}

        <path d={pctPath} fill="none" stroke="#f5f5f4" strokeWidth="2.25" />
        {years.map((y, i) => (
          <g key={'p-' + y.year}>
            <circle cx={xCenter(i)} cy={yLeft(y.pct)} r={3.5} fill="#f5f5f4" stroke="#0a0a0a" strokeWidth="1.5" />
            <text x={xCenter(i)} y={yLeft(y.pct) - 10} fontSize="11" fill="#f5f5f4"
              textAnchor="middle" fontWeight="600">{y.pct.toFixed(2)}%</text>
          </g>
        ))}

        {years.map((y, i) => (
          <text key={'x-' + y.year} x={xCenter(i)} y={H - PAD_B + 16}
            fontSize="11" fill="#94a3b8" textAnchor="middle" fontWeight="500">{y.year}</text>
        ))}

        {years.map((_, i) => (
          <rect key={'hit-' + i}
            x={PAD_L + xBand * i} y={PAD_T}
            width={xBand} height={H - PAD_T - PAD_B}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)} />
        ))}
      </svg>

      {hover !== null && (() => {
        const y = years[hover]
        const placeLeft = hover >= years.length - 2
        const left = placeLeft ? Math.max(0, xCenter(hover) - 220) : xCenter(hover) + 14
        return (
          <div className="absolute pointer-events-none card bg-surface-card/95 backdrop-blur-md border border-border p-3 min-w-[210px] text-xs shadow-xl"
            style={{ left, top: PAD_T + 8 }}>
            <div className="text-base font-bold">{y.year}</div>
            <div className="mt-2 space-y-1 font-mono">
              <RowWithDot dot="solid" color="#f5f5f4" label="Exploit Rate" value={y.pct.toFixed(2) + '%'} />
              <RowWithDot dot="bar"   color="#94a3b8" label="Total CVEs"   value={y.total_cves.toLocaleString()} />
              <RowWithDot dot="bar"   color="#f43f5e" label="Exploited"    value={y.exploited.toLocaleString()} />
            </div>
          </div>
        )
      })()}
    </div>
  )
}

/** Survival curve — one line per year cohort, showing % of eventually-exploited
 *  CVEs still unexploited at each day after disclosure. A 30-day patch-window
 *  annotation is drawn so viewers can see cohorts crossing 50% before the
 *  patch window closes. */
function SurvivalCurveChart({ cohorts }: { cohorts: Record<string, SurvivalPoint[]> }) {
  const W = 900, H = 360
  const PAD_L = 56, PAD_R = 24, PAD_T = 30, PAD_B = 40

  const yearList = useMemo(
    () => Object.keys(cohorts).map(k => parseInt(k, 10)).sort((a, b) => a - b),
    [cohorts],
  )
  const currentYear = yearList.length ? yearList[yearList.length - 1] : 2026

  const colourFor = (year: number): string => {
    const age = currentYear - year
    if (age <= 0) return '#f43f5e'
    if (age === 1) return '#fb7185'
    if (age === 2) return '#fda4af'
    if (age === 3) return '#fed7aa'
    return '#71717a'
  }
  const strokeFor = (year: number): string | undefined => (currentYear - year <= 2 ? undefined : '4 3')

  const maxDay = 90
  const xFor = (d: number) => PAD_L + (d / maxDay) * (W - PAD_L - PAD_R)
  const yFor = (p: number) => PAD_T + (1 - p / 100) * (H - PAD_T - PAD_B)

  return (
    <div className="overflow-x-auto mt-4">
      <div className="flex flex-wrap gap-3 text-[10px] text-text-muted mb-3 px-1">
        {yearList.map(y => (
          <span key={y} className="inline-flex items-center gap-1.5">
            <svg width="16" height="6"><line x1="0" y1="3" x2="16" y2="3"
              stroke={colourFor(y)} strokeWidth="1.75" strokeDasharray={strokeFor(y)} /></svg>
            <span className={clsx(
              'font-mono',
              currentYear - y <= 1 ? 'text-text-primary font-semibold' : '',
            )}>{y}</span>
          </span>
        ))}
      </div>

      <svg width={W} height={H} className="max-w-full">
        {[0, 25, 50, 75, 100].map(p => (
          <g key={p}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yFor(p)} y2={yFor(p)}
              stroke="#27272a" strokeDasharray={p === 0 ? undefined : '2 4'} />
            <text x={PAD_L - 8} y={yFor(p) + 3} fontSize="10" fill="#94a3b8" textAnchor="end">{p}%</text>
          </g>
        ))}

        <line x1={xFor(30)} x2={xFor(30)} y1={PAD_T} y2={H - PAD_B}
          stroke="#94a3b8" strokeDasharray="2 3" opacity="0.5" />
        <text x={xFor(30) + 4} y={PAD_T + 8} fontSize="9" fill="#94a3b8"
          fontWeight="600" letterSpacing="1">30-DAY PATCH WINDOW</text>

        {[0, 7, 14, 30, 60, 90].map(d => (
          <text key={d} x={xFor(d)} y={H - PAD_B + 16} fontSize="10" fill="#94a3b8" textAnchor="middle">
            {d === 0 ? '0' : d === 7 ? '1w' : d === 14 ? '2w' : d === 30 ? '1m' : d === 60 ? '2m' : '3m'}
          </text>
        ))}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 6} fontSize="10" fill="#71717a" textAnchor="middle">
          Days after disclosure
        </text>
        <text x={14} y={PAD_T - 10} fontSize="9" fill="#71717a">% still unexploited</text>

        {yearList.map(y => {
          const pts = cohorts[String(y)] || []
          if (pts.length === 0) return null
          const d = pts.map((pt, i) => (i === 0 ? 'M' : 'L') + xFor(pt.day) + ',' + yFor(pt.pct)).join(' ')
          return (
            <path key={y} d={d} fill="none"
              stroke={colourFor(y)} strokeWidth={currentYear - y <= 1 ? 2.5 : 1.75}
              strokeDasharray={strokeFor(y)}
              strokeLinecap="round" strokeLinejoin="round" />
          )
        })}
      </svg>
    </div>
  )
}
